import { z } from 'zod'
import { SuccessEnvelope, Uuid } from './common.js'

export const IdeaStatus = z.enum(['live', 'processed', 'withdrawn', 'archived'])

// Automated status badge attached to idea cards by badge.service.js. The
// backend sends the single highest-priority badge per card, or `null` when
// the idea matches none. `key` is one of: expert_verified | hot | trending |
// closing_soon | needs_validators.
export const IdeaBadge = z.object({
  key: z.string(),
  label: z.string(),
})

// Slim card. `status`, `submitted_at`, and `review_*` are optional because
// not every endpoint selects them — extend in the per-endpoint schema where
// the field IS guaranteed.
export const IdeaCard = z.object({
  id: Uuid,
  title: z.string(),
  one_line_pitch: z.string().nullable(),
  industry: z.string().nullable(),
  status: IdeaStatus.optional(),
  overall_score: z.number().nullable(),
  number_of_upvotes: z.number(),
  number_of_downvotes: z.number().optional(),
  /** Per-viewer state, attached by list/leaderboard/dashboard endpoints. */
  has_upvoted: z.boolean().optional(),
  has_downvoted: z.boolean().optional(),
  /** Single automated status badge, or null. Attached by list/leaderboard/
   *  dashboard/home endpoints; absent on endpoints that don't compute it. */
  badge: IdeaBadge.nullable().optional(),
  /** Live top-level comment count. Attached by the /ideas list endpoint. */
  feedback_count: z.number().optional(),
  /** Live distinct-viewer count. Attached by the /ideas list endpoint. */
  view_count: z.number().optional(),
  submitted_at: z.union([z.string(), z.date()]).nullable().optional(),
  review_opened_at: z.union([z.string(), z.date()]).nullable().optional(),
  review_closed_at: z.union([z.string(), z.date()]).nullable().optional(),
  review_duration_days: z.number().nullable().optional(),
  submitted_by: z.object({ full_name: z.string() }).nullable().optional(),
})

export const IdeasListResponse = SuccessEnvelope(
  z.object({
    meta: z.object({
      total_on_platform: z.number(),
      total_matching: z.number(),
      facets: z.object({
        industry: z.record(z.string(), z.number()),
        status: z.record(z.string(), z.number()),
      }),
    }),
    page: z.number(),
    total_pages: z.number(),
    ideas: z.array(IdeaCard),
  }),
)

export const SubmitIdeaResponse = z.object({ success: z.literal(true) })

export const ToggleUpvoteResponse = SuccessEnvelope(
  z.object({ upvoted: z.boolean(), downvoted: z.boolean().optional() }),
)

export const ToggleDownvoteResponse = SuccessEnvelope(
  z.object({ downvoted: z.boolean(), upvoted: z.boolean().optional() }),
)

export const IdeaDetail = IdeaCard.extend({
  problem_description: z.string().nullable().optional(),
  problem_experienced_by: z.string().nullable().optional(),
  problem_frequency: z.string().nullable().optional(),
  urgency_level: z.number().nullable().optional(),
  solution_description: z.string().nullable().optional(),
  before_solution: z.string().nullable().optional(),
  after_solution: z.string().nullable().optional(),
  founders_confidence: z.number().nullable().optional(),
  primary_audience: z.string().nullable().optional(),
  specific_audience: z.string().nullable().optional(),
  likelihood_to_pay: z.number().nullable().optional(),
  existing_solutions: z.string().nullable().optional(),
  known_competitors: z.string().nullable().optional(),
  is_anonymous: z.boolean().optional(),
  is_private: z.boolean().optional(),
  creator: z.object({ full_name: z.string() }).nullable().optional(),
  has_upvoted: z.boolean().optional(),
  has_downvoted: z.boolean().optional(),
  validator_count: z.number().optional(),
  feedback_count: z.number().optional(),
  my_validation: z.unknown().nullable().optional(),
})

export const IdeaDetailResponse = SuccessEnvelope(IdeaDetail)
